<template>
  <ym-tabs v-model="tabSelected"
           sticky
           size="medium"
           :offset-top="tabsOffsetTop"
  >
    <ym-tab v-for="(item, index) in navBarList"
            :key="index"
            :name="item.type"
            :title="item.name"
    >
      <template v-if="item.type === 'review'">
        <div slot="title">
          {{ item.name }}
          <span class="ym-tab-title-num"
                v-if="reviews.count > 0"
          >{{ reviews.count > 999 ? '999+' :  reviews.count  }}</span>
        </div>

        <comment-list v-if="showCommentList"
                      :reviews="reviews.list"
                      :next-start="reviews.start"
                      :target-id="product.id"
                      :target-type="productType"
                      :show-come-from="showComeFrom"
        />

        <review-list v-else
                     :reviews="reviews.list"
                     :next-start="reviews.start"
                     :target-id="product.id"
                     :target-type="productType"
        />
      </template>

      <product-introduction v-if="item.type === 'intro'"
                            ref="richTextComponent"
                            :content="product.about"
                            :show-padding="showContentPadding"
                            :percent-grain-list="percentGrainList"
      />

      <program-list v-if="item.type === 'program'"
                    :id="product.id"
                    :updated-count="product.update.count"
                    :update-status="product.update.status"
                    :type="product.type"
                    :order-default="programs.order"
      />

      <lesson-list v-if="item.type === 'lesson'"
                   :course-id="product.id"
                   :list="lessons.list"
                   :member="isMember"
                   :medal="lessons.medal"
                   :sell-status="product.sell.status"
      />

      <work-list v-if="item.type === 'work'"
                 :works="works.list"
                 :next-start="works.start"
                 :query="productInfo"
      />

      <product-introduction v-if="item.type === 'outline'"
                            :content="product.outline"
      />

      <product-introduction v-if="item.type === 'faq'"
                            :content="faq"
      />

      <student-gains-list v-if="item.type === 'studentGains'"
                          :start="studentGains.start"
                          :list="studentGains.list"
                          :id="product.id"
                          :type="productType"
      />
    </ym-tab>
  </ym-tabs>
</template>

<script>
  import { Tabs, Tab } from '@ymba/ym-ui';
  import ProductIntroduction from '@/components/RichText';
  import ReviewList from '@/pages/Review/components/ReviewList';
  import CommentList from '@/components/CommentList/List';
  import ProgramList from '@/pages/Audio/components/AudioDetailProgramList';
  import WorkList from '@/pages/Work/components/WorkList';
  import LessonList from '@/pages/Course/components/CourseDetailLessonList';
  import StudentGainsList from '@/pages/StudentGains/components/StudentGainsExplore';
  import offsetTop from '@/mixins/dom/offset-top';
  import pageScrollPercentMixin from '@/mixins/report/pageScrollPercent';

  export default {
    name: 'ProductDetailContent',
    mixins: [offsetTop, pageScrollPercentMixin],
    components: {
      'ym-tabs': Tabs,
      'ym-tab': Tab,
      ProductIntroduction,
      ReviewList,
      CommentList,
      ProgramList,
      WorkList,
      LessonList,
      StudentGainsList,
    },
    props: {
      value: [String, Number],
      navBarList: {
        type: Array,
        required: true,
        default() {
          return [];
        },
      },
      isMember: Boolean,
      productType: {
        type: String,
        required: true,
      },
      product: Object,
      programs: Object,
      reviews: Object,
      lessons: Object,
      works: Object,
      studentGains: Object,
      showContentPadding: Boolean,
      faq: String,
    },
    data() {
      return {
        tabSelected: 'intro',
      };
    },
    computed: {
      showCommentList() {
        const { productType } = this;
        return [
          'audio',
          'audioCombo',
          'audioLearn',
        ].includes(productType);
      },
      productInfo() {
        const { product, productType: targetType } = this;
        const { id: targetId } = product;
        return {
          targetType,
          targetId,
        };
      },
      showComeFrom() {
        return this.productType === 'audio';
      },
    },
    watch: {
      value(val) {
        this.tabSelected = val;
      },
      tabSelected(val) {
        this.$emit('input', val);
        this.controlScrollHandler(false, val === 'intro');
      },
    },
    methods: {
      controlScrollHandler(init, open) {
        setTimeout(() => {
          if (!this.$refs.richTextComponent) return;
          if (init) {
            const rect = this.$refs.richTextComponent[0].$el.getBoundingClientRect();
            this.pageScrollHandler(rect);
          } else {
            if (open) {
              if (!this.richTextTop || !this.richTextHeight) {
                this.controlScrollHandler(true);
              } else {
                this.pageScrollHandler();
              }
              return;
            }
            this.removeScrollHandler();
          }
        }, init ? 1000 : 0);
      },
    },
    created() {
      this.tabSelected = this.value;
      this.controlScrollHandler(true);
    },
  };
</script>

<style scoped lang="less">
  @import '~@/assets/styles/var.less';

  .ym-tab-flex-end-title {
    display: flex;
    align-items: flex-end;
    line-height: 1;
  }

  .ym-tab-title-num {
    color: @color-text-lighter;
    font-size: @font-size-base;
    font-weight: @font-weight-primary;
    line-height: .9;
    margin-left: @size-extra-small;
  }

  /deep/ .ym-tab {
    min-height: @product-tab-min-height;
  }
</style>
